import { SvgIconKey } from "@/@corefabriccanvas/icons";
import { ReactNode } from "react";

export interface ControlUpdateResponse {
  bookId: number;
  hasUpdate: boolean;
  createdOn: Date;
}

export interface ApiGetBooksResponse {
  books: BookModel[];
  classList: Class[];
  lessonList: Lesson[];
}
export interface ClassAndLessons {
  classList: Class[];
  lessonList: Lesson[];
}

export interface Class {
  id: number;
  name: string;
  title: string;
}

export interface Lesson {
  id: number;
  name: string;
  title: string;
}


export interface PagePathModel {
  imagePath: string;
  width: number;
  height: number;
}

export enum PageNavigationDirection {
  Previous,
  Next,
}

export interface ImagePosition {
  xInCanvas: number;
  yInCanvas: number;
  widthInCanvas: number;
  heightInCanvas: number;
}

export interface PageCanvasModel {
  pageId: number;
  pageNo: number;
  pageImage: string;
  canvasWidth: number;
  canvasHeight: number;
  imageWidth: number;
  imageHeight: number;
  ratio: number;
  pageIcons: PageIcon[];
  pageItems: PageItemModel[];
}

export interface PageItemCanvasModel {
  id: number;
  backgroundImage: string;
  backgroundImageScale: number;
  previousId?: number;
  nextId?: number;
  pageId: number;
  pageNo: number;
  pageImage: string;
  canvasWidth: number;
  canvasHeight: number;
  imageWidth: number;
  imageHeight: number;
  baseLeftPosition: number;
  ratio: number;
  xInCanvas: number;
  yInCanvas: number;
  widthInCanvas: number;
  heightInCanvas: number;
  pageItemType: SbPageItemType;
  pageIcons: PageIcon[];
  sources?: PageIcon[];
  targets?: PageIcon[];
  correctAnswer: string;
  solutionImage: string;
  solutionVideo: string;
  solutionVideoId?: number;
  rootPosition: ImagePosition | null;
  rootPageNo: number;
  order: number;
  isExercise: boolean;
  exerciseType?: unknown;
  hasCompleteButton?: boolean;
  customOptions?: string;
  secondLayer?: string;
}

export interface PageIcon {
  id: string;
  entityId: number;
  pageNumber: number;
  x: number;
  y: number;
  leftMargin: number;
  topMargin: number;
  width: number;
  height: number;
  type: PageIconType;
  icon: SvgIconKey;
  correctAnswer: string;
  iconScale: number;
  title?: string;
  questionId?: number;
  bookId?: number;
  targets?: PageItemModel[];
  group?: number;
  isCorrect?: boolean;
  transcript?: string;
  filePath?: string;
  activityLink?: string;
  isExternalLink?: boolean;
  content?: string;
  isUserContent?: boolean;
  hasSecondLayer?: boolean;
  secondLayer?: string;
  gapFillingOptionImage?: string;
  link?: string;
}

export interface QuestionFile {
  id: number;
  questionId: number;
  fileName: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fileBytes: Uint8Array;
  fileType: QuestionFileType;
  fileTypeStr: string;
  fileUrl: string;
  bookGuid?: string;
  transcript: string;
  isExternalLink: boolean;
}

export enum PageIconType {
  Question = 10,
  ShowGapFillingItem = 20,
  CloseGapFillingItem = 30,
  ShowQuestionAnswer = 40,
  QuestionAnswer = 50,
  Sound = 60,
  Video = 70,
  Url = 80,
  Note = 90,
  Image = 100,
  Html = 110,
  Interacts = 120,
  Games = 130,
  TextNote = 140,
  Drawing = 150,
  File = 160,
}

// SbBookModel.ts
export interface BookModel {
  id: number;
  guid: string;
  title: string;
  defaultTitle: string;
  titleByTenant: string;
  description: string;
  publisherId: number;
  publisher: string;
  classIds: string;
  classId: number;
  classNames: string;
  classOrder: number;
  lessonId: number;
  lessonIds: string;
  lessonNames: string;
  lessonOrder: number;
  category: string;
  displayOrder: number;
  units: SbUnitModel[];
  pages: PageModel[];
  selectedPage: PageModel;
  coverImage: string | null;
  coverImageUrl: string;
  patchCoverImageUrl: string;
  coverImagePath?: string;
  updatedOn: Date;
  isGroup: boolean;
  groupId?: number;
  hasGroup: boolean;
  groupTitle: string;
  expirationInfo: SbExpireDateModel;
  isDownloaded: boolean;
  downloadDate: string;
  hasUpdate: boolean;
  CoverImageUrl: string;
  isExternal: boolean;
  childs: BookModel[];
  bookFiles: BookFile[];
  isPackage?: boolean
}

export interface BookFile {
  id: number;
  bookId: number;
  title: string;
  displayOrder: number;
  fileLink: string;
  bookFileType: number;
}
// SbPageModel.ts
export interface PageModel {
  id: number;
  bookId: number;
  pageNo: number;
  fileId: number;
  pageImage: string;
  pageImageUrl: string;
  pagePath: string;
  contentName: string;
  pageItems: PageItemModel[];
  updatedOn: Date;
  isSolutionPage: boolean;
}

// SbUnitModel.ts
export interface SbUnitModel {
  id: number;
  name: string;
  bookName: string;
  description: string;
  code: string;
  startPage: number;
  endPage: number;
  parentId?: number; // Optional field
  bookId: number;
  subUnits: SbUnitModel[]; // Recursive structure
  unitContentModels: SbUnitContentModel[];
}

export interface SbUnitContentModel {
  id: number;
  name: string;
  description: string;
  code: string;
  displayOrder: number;
  unitId: number;
  startPage?: number; // Optional field
  endPage?: number; // Optional field
  quizId?: number; // Optional field
  courseId?: number; // Optional field
  contentType: UnitContentTypeEnum;
}

export enum UnitContentTypeEnum {
  Course = 10,
  Quiz = 20,
  Sound = 30,
  Video = 40,
  Url = 50,
  Html = 60,
}

// SbPageItemModel.ts
export interface PageItemModel {
  id: number;
  questionId?: number;
  bookId: number;
  bookContentId?: number;
  smId: string;
  testId: number;
  imageId?: number;
  title: string;
  isCorrect: boolean;
  imageBytes: string;
  imageUrl: string;
  imagePath?: string;
  position?: SbPageItemPosition;
  rootImageBytes: Uint8Array;
  rootPosition?: SbPageItemPosition;
  pageItemType: SbPageItemType;
  pageItemTypeStr: string;
  contentId?: number;
  correctAnswer: string;
  options: PageItemModel[];
  pageNumber: number;
  activityLink: string;
  order: number;
  solutionImage: string;
  solutionVideo: string;
  solutionVideoId?: number;
  solutionType: SolutionType;
  solutionVideoType: SolutionVideoType;
  optionCount: number;
  updatedOn: Date;
  linkToPageNo?: number;
  gameLink: string;
  link: string;
  isTarget: boolean;
  group?: number;
  sources: PageItemModel[];
  targets: PageItemModel[];
  setting: SbPageItemSetting;
  files: SbPageItemFile[];
  transcript: string;
  childs: PageItemModel[];
  secondLayer: string;
}

// SbPageItemPosition.ts
export interface SbPageItemPosition {
  id: number;
  axisX: number;
  axisY: number;
  width: number;
  height: number;
  imageWidth: number;
  imageHeight: number;
  page: number;
  questionId?: number;
  rootQuestionId?: number;
  optionId?: number;
  activityId?: number;
}

// SbPageItemSetting.ts
export interface SbPageItemSetting {
  id: number;
  bookId: number;
  hasCompleteButton: boolean;
  hasSoundAlert: boolean;
  customOptions: string;
  questionId: number;
}

// SbPageItemFile.ts
export interface SbPageItemFile {
  id: number;
  activityLink: string;
  questionId: number;
  fileName: string;
  position: SbPageItemPosition;
  fileBytes: Uint8Array;
  fileType: QuestionFileType;
  fileTypeStr: string;
  fileUrl: string;
  bookGuid?: string;
  transcript: string;
  isExternalLink: boolean;
}

// SbExpireDateModel.ts
export interface SbExpireDateModel {
  expireDate?: Date;
  isExpired: boolean;
}

// Enums.ts
export enum SbPageItemType {
  MultipleChoice = 0,
  Subject = 10,
  GapFilling = 20,
  Image = 30,
  Sound = 40,
  Video = 50,
  Html = 60,
  Link = 70,
  OpenEnded = 80,
  DragDrop = 90,
  MarkObject = 100,
  Matching = 110,
  TrueFalse = 120,
  Painting = 130,
  Underline = 140,
  FindCorrectOne = 150,
  Tick = 160,
  PageLink = 170,
  Game = 180,
}

export enum SolutionType {
  NoSolution = 0,
  Image = 10,
  Video = 20,
  Both = 30,
}

export enum SolutionVideoType {
  None,
  Mp4,
  Vector,
  External,
}

export enum QuestionFileType {}
// Add specific file types here if needed

export enum SubToolType {
  Pen = "pen",
  Marker = "marker",
  Shapes = "shapes",
  Eraser = "eraser",
}

export enum PageItemPosition {
  left = "left",
  center = "center",
  right = "right",
}

export enum EraserShortCuts {
  Closed = "closed",
  DoubleTap = "doubletap",
  LongPress = "longpress",
}
export enum DisplayTypes {
  Page = "page",
  PageItem = "pageItem",
  BlankPage = "blankPage",
  CroppedArea = "croppedArea",
}

export enum UserContentType {
  Text = "text",
  Image = "image",
  Video = "video",
  Sound = "sound",
  Drawing = "drawing",
  Link = "link",
  File = "file",
}

export enum PackageType {
  USER_LOGIN = "USER_LOGIN",
  PUBLIC = "PUBLIC",
  CODE_LOGIN = "CODE_LOGIN",
  SINGLE_EXE = "SINGLE_EXE",
  SET_BOOK = "SET_BOOK",
}

export type ChildrenType = {
  children: ReactNode;
};
